import { CalendarIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { Calendar } from "@fronterahealth/frontera-ui-components/ui/calendar";
import { Input, inputVariants } from "@fronterahealth/frontera-ui-components/ui/input";
import { Label } from "@fronterahealth/frontera-ui-components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@fronterahealth/frontera-ui-components/ui/popover";

import { ApiReportSectionFieldsFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import {
  formatDisplayDatetime,
  isDateFieldContentValid,
  parseStoredDatetime,
  toTimeInputValue,
  withTimeInputValue,
  withTimeOfDay,
} from "@components/ReportField/inputs/dateFieldUtils";
import { ReportFieldInputProps } from "@components/ReportField/types";

export const ReportFieldDatetime: React.FC<ReportFieldInputProps> = ({
  id,
  value,
  onChange,
  variant,
  placeholder,
  disabled,
  onFocus,
  onBlur,
  ariaInvalid,
}) => {
  const [open, setOpen] = useState(false);
  const selected = parseStoredDatetime(value);
  const display = formatDisplayDatetime(selected);
  // A picked datetime or an unreadable leftover; an unresolved placeholder token is left for the server to fill.
  const canClear = !!selected || !isDateFieldContentValid(ApiReportSectionFieldsFieldTypeChoices.Datetime, null, value);

  if (disabled) {
    return <ReportFieldDisabledValue value={display || value} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            onFocus={onFocus}
            onBlur={onBlur}
            aria-invalid={ariaInvalid}
            className={cn(
              inputVariants({ variant }),
              "w-full justify-between font-normal",
              !display && !value && "text-static-text-subtle",
            )}
          >
            {display || value || placeholder || "MM/DD/YYYY hh:mm AM"}
            <CalendarIcon
              className={cn(
                "size-4 shrink-0 opacity-60 transition-opacity group-hover/button:opacity-60 group-focus-visible/button:opacity-60 group-aria-expanded/button:opacity-60",
                variant === "ghost" && "opacity-0",
              )}
            />
          </Button>
        }
      />
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(day) => {
            if (!day) return;
            onChange(withTimeOfDay(day, selected).toISOString());
          }}
          captionLayout="dropdown"
        />
        <div className="flex items-center justify-between gap-3 border-t p-3">
          <Label htmlFor={`${id}-time`} className="text-sm font-normal text-static-text-subtle">
            Time
          </Label>
          <Input
            id={`${id}-time`}
            type="time"
            className="w-fit"
            disabled={!selected}
            value={toTimeInputValue(selected)}
            onChange={(e) => {
              if (!selected) return;
              const next = withTimeInputValue(e.target.value, selected);
              if (next) onChange(next.toISOString());
            }}
          />
        </div>
        <div className="flex border-t p-2">
          {canClear && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                void onChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
